import { NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  BlacklistWarningItem,
  ButtonSecondary,
  ButtonSize,
  IconChangeLoClock,
  PermissionEnum,
  Popup,
  Select,
  TransactionWarningActionEnum,
  MatchStatusEnum,
  Textarea,
  ButtonPseudo,
} from '@ui/src';
import { useAppSelector } from '@store';
import { CustomerStatus, OrganizationType } from '../types';
import { OngoingPanelContentTypes } from './types';

import cn from './ongoing-monitoring.module.css';

const OngoingPanelContent = ({
  warnings,
  blacklists,
  setOngoingItemPanelVisible,
  setBlacklistDataVisible,
  setBlacklistData,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  type,
  applyError,
  applyWarningInProgress,
  applyOngoingWarning,
  warningGroupId,
  batchApplyWarningInProgress,
  batchApplyOngoingWarning,
  batchApplyWarningIsSuccess,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  selectedWarningId,
  status,
  warningsPagination,
  getWarningsAction,
  paginationInProgress,
  selectedWarningGroup,
  getOngoingWarningById,
  isWarningByIdLoading,
  isWarningByIdFetching,
}: OngoingPanelContentTypes) => {
  const [applyAllPopupVisible, setApplyAllPopupVisible] = useState(false);
  const [matchStatus, setMatchStatus] = useState('');
  const [action, setAction] = useState('');
  const [comment, setComment] = useState('');

  const {
    customersDictionaries: { currentEmployee, activePermissions },
  } = useAppSelector((state) => state);

  let aproveAllowed = false;
  let rejectAllowed = false;

  const batchApplyDisabled = !warnings?.filter((item) => !item.action)?.length;

  if (
    type === OrganizationType.INDIVIDUAL &&
    activePermissions?.includes(
      PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_APPROVE_ACTION,
    )
  ) {
    aproveAllowed = true;
  } else if (
    type === OrganizationType.CORPORATE &&
    activePermissions?.includes(
      PermissionEnum.CORPORATE_CUSTOMERS_AML_APPROVE_ACTION,
    )
  ) {
    aproveAllowed = true;
  }

  if (
    type === OrganizationType.INDIVIDUAL &&
    activePermissions?.includes(
      PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_REJECT_ACTION,
    )
  ) {
    rejectAllowed = true;
  } else if (
    type === OrganizationType.CORPORATE &&
    activePermissions?.includes(
      PermissionEnum.CORPORATE_CUSTOMERS_AML_REJECT_ACTION,
    )
  ) {
    rejectAllowed = true;
  }

  const onApplyActions = async () => {
    await batchApplyOngoingWarning({
      id: warningGroupId,
      action,
      comment,
      matchStatus,
    });
  };

  useEffect(() => {
    if (batchApplyWarningIsSuccess) {
      setApplyAllPopupVisible(false);
      setComment('');
      setMatchStatus('');
      setAction('');
    }
  }, [batchApplyWarningIsSuccess]);

  return (
    <div className={cn.ongingPannelContent}>
      <div className={cn.ongoinActionsTitle}>
        <div>Found matches that require a solution</div>
        {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
          <NavLink
            to={`/administration/change-logs?entityType=WARNING_GROUP&entityId=${warningGroupId}`}
            className={cn.applicationLink}
            target="_blank"
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
      </div>
      {(aproveAllowed || rejectAllowed) &&
        !batchApplyDisabled &&
        status !== CustomerStatus.CLOSED && (
          <ButtonSecondary
            size={ButtonSize.SMALL}
            text="Apply to all matches"
            className={`button-white ${cn.applyAll}`}
            onClick={() => setApplyAllPopupVisible(true)}
          />
        )}
      <div className={cn.matchesWrapper}>
        {warnings?.map((item) => (
          <BlacklistWarningItem
            key={item.id}
            warning={item}
            employee={currentEmployee}
            onApplyTransactionAction={(data) => {
              applyOngoingWarning({
                id: warningGroupId,
                warningId: data.warningId,
                action: data.body.action,
                comment: data.body.comment,
                matchStatus: data.body.matchStatus,
              });
            }}
            setBlacklistDataVisible={() => {
              setOngoingItemPanelVisible(false);
              setBlacklistDataVisible(true);
            }}
            setBlacklistDataItem={(data) => setBlacklistDataItem(data)}
            setBlacklistItemVisible={() => {
              setOngoingItemPanelVisible(false);
              setBlacklistItemVisible(true);
            }}
            setBlacklistData={(data) => setBlacklistData(data)}
            activePermissions={activePermissions}
            hideApproveActions={!aproveAllowed}
            hideRejectActions={!rejectAllowed}
            applyTransactionsWarningIsLoading={applyWarningInProgress}
            onResetTransactionWarningAction={(id) => {
              if (onResetTransactionWarningAction) {
                onResetTransactionWarningAction(id, warningGroupId);
              }
            }}
            resetTransactionsWarningIsLoading={
              resetTransactionsWarningIsLoading
            }
            resetTransactionsWarningIsSuccess={
              resetTransactionsWarningIsSuccess
            }
            warningInGroupId={selectedWarningId}
            withoutActions={status === CustomerStatus.CLOSED}
            getWarningById={getOngoingWarningById}
            entityId={warningGroupId}
            isWarningByIdFetching={isWarningByIdFetching}
            isWarningByIdLoading={isWarningByIdLoading}
            globalCollapsed
            isPaymentOrder
            withoutChat
          />
        ))}
        {getWarningsAction && warningsPagination?.hasNextPage && (
          <div className={cn.warningsGroupPagination}>
            <ButtonPseudo
              text={
                selectedWarningGroup === warningGroupId && paginationInProgress
                  ? 'Loading...'
                  : 'Show more'
              }
              disabled={paginationInProgress}
              onClick={() =>
                getWarningsAction({
                  warningGroupId,
                  cursor: warningsPagination?.endCursor,
                })
              }
              bold
            />
          </div>
        )}
        {applyError && <div className={cn.applyError}>{applyError}</div>}
      </div>
      <div className={cn.ongoinActionsTitle}>Checked blocklists</div>
      <ul className={cn.blacklists}>
        {blacklists?.map((item) => (
          <li key={item.split(' ').join('')}>{item}</li>
        ))}
      </ul>
      <Popup
        title="Select status and action"
        description="This will be applied to all matches in the group"
        onCancelClick={() => {
          setApplyAllPopupVisible(false);
          setComment('');
          setMatchStatus('');
          setAction('');
        }}
        onOkClick={onApplyActions}
        cancelButtonText="Cancel"
        okButtonText={
          batchApplyWarningInProgress ? 'Loading...' : 'Apply to all'
        }
        okButtonDisabled={
          !comment || !action || !matchStatus || batchApplyWarningInProgress
        }
        isVisible={applyAllPopupVisible}
        parent={document.getElementById('apply-all-popup') as HTMLElement}
      >
        <div className={cn.applyAllContent}>
          <Select
            name="matchStatus"
            value={matchStatus}
            onChange={(e) => setMatchStatus(e.target.value as string)}
            list={[
              {
                name: 'False',
                value: MatchStatusEnum.FALSE,
              },
              {
                name: 'True',
                value: MatchStatusEnum.TRUE,
              },
              {
                name: 'Potential',
                value: MatchStatusEnum.POTENTIAL,
              },
            ]}
            placeholder="Select Match status"
            disabled={batchApplyDisabled || batchApplyWarningInProgress}
            className={cn.matchStatusSelect}
            fullWidth
          />
          <Select
            name="actions"
            value={action}
            onChange={(e) => setAction(e.target.value as string)}
            list={[
              {
                name: 'Pass all time for this client',
                value:
                  TransactionWarningActionEnum.PASS_ALL_TIME_FOR_THIS_CLIENT,
                hidden: !aproveAllowed,
              },
              {
                name: 'Pass combination',
                value: TransactionWarningActionEnum.PASS_COMBINATION,
                hidden: !aproveAllowed,
              },
              {
                name: 'Pass once',
                value: TransactionWarningActionEnum.PASS_ONCE,
                hidden: !aproveAllowed,
              },
              {
                name: 'Stop',
                value: TransactionWarningActionEnum.STOP,
                hidden: !rejectAllowed,
              },
            ]}
            placeholder="Select actions"
            className={cn.matchStatusSelect}
            disabled={batchApplyDisabled || batchApplyWarningInProgress}
            fullWidth
          />
          <Textarea
            name="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter a comment..."
            disabled={batchApplyDisabled || batchApplyWarningInProgress}
          />
        </div>
      </Popup>
    </div>
  );
};

export default OngoingPanelContent;
